import type { ReactNode } from "react";
import { useRouterState } from "@tanstack/react-router";
import { cn } from "@/lib/cn";
import { useRailCollapsed } from "@/lib/nav";
import { useUser } from "@/lib/queries";
import { useWide } from "@/lib/useMediaQuery";
import { InRailContext } from "../rail";
import { useCounts } from "./counts";
import { areaOf, navFor, sectionOf } from "./navItems";
import { SectionChips, SectionRail, type RailGroup } from "./SectionRail";

/**
 * One way to move between an area's sections, everywhere: a rail pinned to
 * the left edge on a desk (it folds to icons, `[` too), a row of chips on a
 * phone. The course bar above still picks the area.
 */
export function RailLayout({
  title,
  groups,
  active,
  top,
  children,
}: {
  title: string;
  groups: RailGroup[];
  active: string | undefined;
  top?: ReactNode;
  children: ReactNode;
}) {
  const wide = useWide();
  const [collapsed, toggle] = useRailCollapsed();
  if (!wide) {
    return (
      <>
        <div className="px-3.5 pt-3 print:hidden">
          <SectionChips groups={groups} active={active} />
        </div>
        {children}
      </>
    );
  }
  return (
    <div className="flex min-h-full items-stretch">
      <aside
        className={cn(
          "min-h-[calc(100dvh-150px)] shrink-0 border-r border-line bg-surface px-2 pt-5 transition-[width] duration-[180ms] ease-out motion-reduce:transition-none print:hidden",
          collapsed ? "w-[68px]" : "w-[240px]",
        )}
      >
        <SectionRail
          title={title}
          groups={groups}
          active={active}
          collapsed={collapsed}
          onToggle={toggle}
          top={top}
        />
      </aside>
      <div className="min-w-0 flex-1">
        <InRailContext.Provider value>{children}</InRailContext.Provider>
      </div>
    </div>
  );
}

/** Wraps every signed-in page: areas with two or more sections get the rail. Settings draws its own. */
export function AreaFrame({ children }: { children: ReactNode }) {
  const me = useUser();
  const path = useRouterState({ select: (s) => s.location.pathname });
  const counts = useCounts();
  const area = navFor(me.role).find((a) => a.id === areaOf(path));
  const sections = area?.sections ?? [];
  // Running a check is the whole screen on a phone and a focused one on a desk.
  const running = /^\/checks\/tasks\/[^/]+\/run/.test(path);
  if (!area || sections.length < 2 || running) return <>{children}</>;
  const groups: RailGroup[] = [
    {
      label: "",
      items: sections.map((s) => ({
        to: s.to,
        label: s.label,
        icon: s.icon,
        badge: counts.section[s.to],
      })),
    },
  ];
  return (
    <RailLayout
      title={area.label}
      groups={groups}
      active={sectionOf(sections, path)?.to}
    >
      {children}
    </RailLayout>
  );
}
